
import type {
  ShuffleArrayItem,
  HtmlEvaluationResult,
  PromiseConditionOptions,
  TimeoutObject,
  ObjectPropertyValue,
} from '@packages/utils/types/generic.ts'

const toNormalForm = (str: string): string => `${str || ''}`.normalize('NFD').replace(/[\u0300-\u036F]/g, '')

const toSnakeCase = (str: string): string => toNormalForm(str)
  .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
  ?.map(x => x.toLowerCase())
  ?.join('-') || ''

const truncateByWords = (text: string, wordCount = 50, glue = '...'): string => `${text || ''}`
  .trim()
  .split(' ')
  .slice(0, wordCount)
  .map((e, i) => i < wordCount - 1 ? e : glue)
  .join(' ')

const truncateTripName = (name: string, length = 14): string => {
  if (!name) {
    return ''
  }
  return name.length > length ? name.substring(0, length) + '...' : name
}

/**
 * Reason attached to an abort that was triggered by the request's own timeout rather than
 * by a caller cancelling it. Mirrors ofetch's built-in timeout error (name 'TimeoutError',
 * same wording) so both timeout paths classify identically downstream — see
 * isTimeoutAbortError in @layers/web/utils/error.ts. A caller-triggered abort keeps the
 * platform default reason (a DOMException named 'AbortError'), which is what tells the two
 * apart: a timeout is a real failure worth reporting, a cancellation is not.
 */
const createTimeoutAbortReason = (): Error => {
  const reason = new Error('The operation was aborted due to timeout')
  reason.name = 'TimeoutError'
  return reason
}

/**
 * There's no vanilla js way to have both an AbortController.signal (no timeout feature)
 * and AbortSignal.timeout (no abort feature) on a request
 * @param abortController AbortController instance
 * @param ms Abort after ms
 * @returns Clearable timeout instance
 */
const abortTimeout = (abortController: AbortController, ms: number): ReturnType<typeof setTimeout> | false => {
  if (typeof window === 'undefined') {
    return false
  }
  return setTimeout(() => abortController?.signal.aborted ? null : abortController.abort(createTimeoutAbortReason()), ms)
}

/**
 * Creates an AbortSignal that automatically aborts after a specified timeout.
 *
 * The abort reason distinguishes the two paths: the timeout aborts with a 'TimeoutError',
 * while a linked abort forwards the linked signal's own reason so a caller cancellation stays
 * recognisable as one.
 *
 * @param timeout - The timeout in milliseconds after which the signal will be aborted.
 * @param linkedSignal - Optional signal (e.g. a cancellation controller) that also aborts the returned signal.
 * @returns The AbortSignal that will be aborted after the specified timeout or when the linked signal aborts.
 */
function createTimeoutSignal (timeout: number, linkedSignal?: AbortSignal): AbortSignal {
  const controller = new AbortController()
  const abort = (reason: unknown) => controller.signal.aborted ? null : controller.abort(reason)
  const timeoutId = setTimeout(() => abort(createTimeoutAbortReason()), timeout)

  controller.signal.addEventListener('abort', () => clearTimeout(timeoutId), { once: true })
  linkedSignal?.addEventListener('abort', () => abort(linkedSignal.reason), { once: true })

  if (linkedSignal?.aborted) {
    abort(linkedSignal.reason)
  }

  return controller.signal
}
const replaceCarriageReturns = (str: string): string => str.replace(/\r/g, '\n')

const replaceSpacesWithPlusSigns = (value: string): string => value.replace(/ /g, '+')

const applyUnixNewlinesToObjectKeys = (obj: Record<string, string>, objectKeys: string[]): Record<string, string> => ({
  ...obj,
  ...Object.entries(obj)
    .filter(([key, val]) => objectKeys.includes(key) && typeof val === 'string')
    .reduce((acc, [key, val]) => ({
      ...acc,
      [key]: replaceCarriageReturns(val),
    }), {} as Record<string, string>),
})

const applyUnixNewlinesToArrayItem = (
  item: Record<string, string> | string,
  objectKeys: string[]
): Record<string, string> | string => {
  if (typeof item === 'object') {
    return applyUnixNewlinesToObjectKeys(item, objectKeys)
  }
  return replaceCarriageReturns(item)
}

/**
 * Replace DOS newlines (\r) with UNIX / HTML interpreter newlines (\n)
 * Useful for:
 * - Using css "white-space: pre-line" will render newlines
 * - Fixing text hydration mismatch caused by text from FileMaker with \r being removed/ignored by browser
 * @param value \r as newline symbol
 * @param objectKeys keys in value object/value array item object to process
 * @returns \n as newline symbol
*/
const toUnixNewlines = <T>(value: T, objectKeys: string[] = []): T => {
  if (!value) {
    return value
  }

  if (typeof value === 'string') {
    return replaceCarriageReturns(value) as T
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (!item) {
        return item
      }
      return applyUnixNewlinesToArrayItem(item as Record<string, string> | string, objectKeys)
    }) as T
  }

  if (typeof value === 'object') {
    return applyUnixNewlinesToObjectKeys(value as Record<string, string>, objectKeys) as T
  }

  return value
}
/**
 * Randomize order of array entries
 * @param array to randomize order
 * @returns array with randomized order
 */
const shuffleArray = <T>(array: T[]): T[] => array
  .map((value): ShuffleArrayItem<T> => ({ value, sort: Math.random() }))
  .sort((a, b) => a.sort - b.sort)
  .map(({ value }) => value)

const htmlEvaluator = (html: string): HtmlEvaluationResult | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const cleanedHtml = `${html || ''}`
    .replace(/\r/g, '\n')
    .replace(/ & /g, ' &amp; ')
  const element = document.createElement('div')
  element.innerHTML = cleanedHtml

  return {
    element,
    cleaned: cleanedHtml,
    raw: html,
    isValid: element.innerHTML === cleanedHtml,
  }
}

const stringIsValidHtml = (html: string): boolean => htmlEvaluator(html)?.isValid || false

const stringContainsHtml = (html: string): boolean => {
  const evaluation = htmlEvaluator(html)

  if (!evaluation) {
    return false
  }

  return Array.from(evaluation.element.childNodes).some(node => node.nodeType === 1)
}

function loadScript (src: string, id: string, callback: () => void): void {
  const existingScriptElem = document.getElementById(id) as (HTMLScriptElement & { loaded?: boolean }) | null

  if (existingScriptElem?.loaded) {
    callback()
    return
  }

  if (existingScriptElem) {
    existingScriptElem.addEventListener('load', callback)
    return
  }

  const scriptElem = document.createElement('script') as HTMLScriptElement & { loaded?: boolean }
  scriptElem.addEventListener('load', () => {
    scriptElem.loaded = true
    callback()
  })

  scriptElem.setAttribute('src', src)
  scriptElem.setAttribute('id', id)
  document.head.appendChild(scriptElem)
}

const formatNumber = (num: number): string => num?.toLocaleString('fr')

const findActiveSlugKey = (localeURLs: Record<string, string> = {}, params: Record<string, string | string[]> = {}, slug = 'tab'): string => {
  const slugValue = Array.isArray(params[slug]) ? params[slug][0] : params[slug]
  // The tables hold the tab paths ('/', '/kontakt'); a missing slug is the table's '/' entry.
  const slugPath = `/${slugValue ?? ''}`

  return Object.keys(localeURLs)
    .find(key => localeURLs[key] === slugPath) ?? ''
}

const promiseUntilCondition = (condition: () => boolean | Promise<boolean>, opts: PromiseConditionOptions = {}): Promise<void> => new Promise((resolve, reject) => {
  Promise.resolve(condition()).then(result => {
    if (result) {
      resolve()
      return
    }

    const { retries, interval, abortSignal } = {
      retries: 200,
      interval: 150,
      abortSignal: null,
      ...opts,
    }

    const counter = { value: 0 }
    const checkConditionInterval = window.setInterval(() => {
      counter.value++
      if (abortSignal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        clearInterval(checkConditionInterval)
        return
      }
      if (counter.value > retries) {
        reject(new Error(`Could not fill condition: ${condition.toString()}`))
        clearInterval(checkConditionInterval)
        return
      }
      Promise.resolve(condition()).then(result => {
        if (!result) {
          return
        }
        resolve()
        clearInterval(checkConditionInterval)
      })
    }, interval)
  })
})

const capitalizeFirstLetter = (s: string): string => !!s && typeof s === 'string' ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const capitalizeWords = (s: string): string =>
  s.toLowerCase().replace(/\b\w/g, char => char.toUpperCase())

const deepClone = <T>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value)) as T
  } catch {
    return Array.isArray(value) ? [...(value || [])] as T : { ...(value || {}) } as T
  }
}
const timeout = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

const isEmpty = (value: string | number | boolean | object | null | undefined | []): boolean => (
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && Object.values(value || {}).length === 0)
)

/**
 * @param obj
 * @param properties
 * @param filterEmpty
 * @returns obj only with keys from properties
 */
const getObjectOnlyProperties = (obj: object, properties: string[], filterEmpty = false): Record<string, ObjectPropertyValue> => {
  const record = obj as Record<string, ObjectPropertyValue>
  const activeProperties = filterEmpty
    ? properties.filter(key => !isEmpty(record[key]))
    : properties

  return activeProperties.reduce<Record<string, ObjectPropertyValue>>((acc, key) => ({
    ...acc,
    [key]: record[key],
  }), {})
}
/**
 * Split array into specified amount per chunk
 * @param array
 * @param perChunk
 * @param even
 */
const chunkArray = <T>(array: T[], perChunk: number, even?: boolean): T[][] => {
  const chunks = (array || []).reduce<T[][]>((acc, value) => {
    const lastChunk = acc[acc.length - 1]
    if (!lastChunk || lastChunk.length === perChunk) {
      return [...acc, [value]]
    }
    return [...acc.slice(0, -1), [...lastChunk, value]]
  }, [])

  if (!even || !chunks.length || !chunks[0].length || chunks[chunks.length - 1].length === perChunk) {
    return chunks
  }

  const lastChunk = chunks[chunks.length - 1]
  const paddingElements = array.slice(0, perChunk - lastChunk.length)
  return [...chunks.slice(0, -1), [...lastChunk, ...paddingElements]]
}

/**
 * Split array into specified amount of groups
 * @param array
 * @param groups
 */
const groupArray = <T>(array: T[], groups: number): T[][] => {
  const itemsPerGroup = Math.floor(array.length / groups)
  const remainder = array.length % groups

  return Array.from({ length: groups }, (_, i) => {
    const start = i * itemsPerGroup + Math.min(i, remainder)
    const end = start + itemsPerGroup + (i < remainder ? 1 : 0)
    return array.slice(start, end)
  })
}

const uniqueArray = <T>(array: T[], key?: keyof T): T[] => {
  if (!key) {
    return [...new Set(array)]
  }

  return array.filter((item, index) =>
    array.findIndex(other => other[key] === item[key]) === index
  )
}

const trimByWord = (str: string, limit = 160): string => {
  if (typeof str !== 'string' || str.length <= limit) {
    return str
  }

  return str.substring(0, str.lastIndexOf(' ', limit))
}

const removeTags = (str: string | null): string | false => {
  if (str === null || str === '') {
    return false
  }

  return str.toString().replace(/(<([^>]+)>)/ig, '').trimStart()
}

/**
 * Does not abort promise
 */
const waitMaxMs = <T>(promise: Promise<T>, ms: number): Promise<T> | false => {
  if (typeof window === 'undefined') {
    return false
  }
  return new Promise<T>((resolve, reject) => {
    const waitUntilReject = setTimeout(reject, ms)

    promise
      .then((returns) => {
        clearTimeout(waitUntilReject)
        resolve(returns)
      })
      .catch(reject)
  })
}

const waitMs = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const generateId = (): string => Math.round(Math.random() * 1e15).toString()

const isBodyOverflowHidden = (): boolean => Array.from(document.body.classList)
  .includes('overflow-hidden')

const setLongTimeout = (callback: () => void, duration: number): TimeoutObject => {
  const timeout: TimeoutObject = { id: null }

  const runNextInterval = (remainingTime: number): void => {
    if (remainingTime > 2147483647) {
      timeout.id = setTimeout(() => runNextInterval(remainingTime - 2147483647), 2147483647)
      return
    }
    timeout.id = setTimeout(callback, remainingTime)
  }

  runNextInterval(duration)
  return timeout
}

const copyText = (text: string) => navigator.clipboard.writeText(text)

export {
  toNormalForm,
  toSnakeCase,
  truncateByWords,
  truncateTripName,
  abortTimeout,
  createTimeoutSignal,
  toUnixNewlines,
  replaceSpacesWithPlusSigns,
  shuffleArray,
  stringIsValidHtml,
  stringContainsHtml,
  loadScript,
  formatNumber,
  findActiveSlugKey,
  promiseUntilCondition,
  capitalizeFirstLetter,
  capitalizeWords,
  deepClone,
  timeout,
  isEmpty,
  getObjectOnlyProperties,
  chunkArray,
  groupArray,
  uniqueArray,
  trimByWord,
  removeTags,
  waitMaxMs,
  waitMs,
  generateId,
  isBodyOverflowHidden,
  setLongTimeout,
  copyText,
}